import {
    apiCalculateSubscriptionEditOffersPrice,
    apiCalculateSubscriptionOffersPrice,
    SubscriptionOfferStore
} from "../../../client/state/SubscriptionOfferStore";
import {Dispatchable, Dispatcher} from "../../../stem-core/src/base/Dispatcher";
import {SOUTHERN_THING_ALIAS} from "../../Constants"
import {iFrameState} from "../../services/IFrameState";
import {AnalyticsEventType, dispatchAnalyticsEvent} from "../../../blink-sdk/utils/AnalyticsClient";
import {LoadingSpinner} from "../../ui/LoadingSpinner";
import {
    apiCancelSubscription,
    apiChangeSubscription,
    apiSubscribe, apiSubscribeWithCds,
    SubscriptionApplyPolicy
} from "../../../client/state/SubscriptionStore";
import {Messages} from "../../Messages";
import {Router} from "../../../stem-core/src/ui/Router";
import {INLINE_EDIT_SUBSCRIPTION_URLS, INLINE_SUBSCRIPTION_URLS} from "../PanelConstants";
import {iFrameMerchantService} from "../../services/IframeMerchantService";
import {iFrameUserDataService} from "../../services/IFrameUserDataService";
import {PanelToast} from "../PanelToast";
import {FLOW_CHECKPOINT, FLOW_TYPE, PANEL_TYPE, PANEL_VARIANT} from "../../../blink-sdk/Constants";
import {paymentMethodController} from "../inline-donation/PaymentMethodController";
import {PaymentMethodStore} from "../../../client/state/PaymentMethodStore";
import {CARD_SECTION_STATE} from "../inline-donation/PaymentMethodController";
import {addressController} from "./AddressController";
import {ADDRESS_SECTION_STATE} from "../pages/panels/address/AddressPanel";
import {SUBSCRIBE_PLAN_STATE, subscribePlanController} from "./SubscribePlanController";
import {collapseIframe} from "../../widget/misc/WidgetUtils";
import {sendFlowCheckpoint} from "../flows/FlowController";


export async function fetchSubscriptionPrices() {
    LoadingSpinner.show();
    if (subscribePlanController.subscribePlanState === SUBSCRIBE_PLAN_STATE.editSubscription) {
        await subscribePlanController.fetchSubscriptionEditPrices();
    } else {
        await subscribePlanController.checkDiscountCode();
    }
    LoadingSpinner.hide();
}


class SubscribeFlowState extends Dispatchable {
    init() {
        console.assert(iFrameState.panelType === PANEL_TYPE.subscribe);

        this.subscriptionToCancel = null;



        if (iFrameState.variant === PANEL_VARIANT.editRecurring && iFrameUserDataService.getActiveSubscription()) {
            // TODO: Update the edit subscription flow to use the common payment method component.
            this.updateFromActiveSubscription();
        } else {
            paymentMethodController.update({
                cardSectionState: CARD_SECTION_STATE.chooseCard,
                initialPaymentMethod: paymentMethodController.getInitialPaymentMethod(),
                selectedPaymentMethod: paymentMethodController.getInitialPaymentMethod(),
                savePaymentMethod: true,
                getChooseCardButtonLabel: () => Messages.finalizePayment,
                getPayWithAddedCardButtonLabel: () => Messages.finalizePayment,
                getScope: () => PaymentMethodStore.Scope.RECURRING_PAYMENT,
                onSelectSuccess: () => subscribeFlowState.subscribe(),
            });

            subscribePlanController.update({
                subscribePlanState: SUBSCRIBE_PLAN_STATE.subscribe,
                selectedOffer: null,
                onSelectSuccess: async () => {
                    // If we come from login page and switch to review, we want the back to take us to the plans
                    // page. Currently, this is how we achieve that without disrupting the flow in any way.
                    Router.localHistory.push(INLINE_SUBSCRIPTION_URLS.subscriptionsOverview);
                    const subscriptionOffer = subscribePlanController.selectedOffer;

                    if (!subscriptionOffer.shouldCollectAddress()) {
                        await fetchSubscriptionPrices();
                        Router.changeURL(INLINE_SUBSCRIPTION_URLS.subscriptionCheckout);
                        return;
                    }

                    Router.changeURL(INLINE_SUBSCRIPTION_URLS.address);
                },
                onCancel: collapseIframe,
            });

            addressController.update({
                addressSectionState: ADDRESS_SECTION_STATE.chooseAddress,
                onSelectSuccess: async () => {
                    await fetchSubscriptionPrices();
                    Router.changeURL(INLINE_SUBSCRIPTION_URLS.subscriptionCheckout)
                },
                onSelectCancel: () => Router.back(),
                continueLabel: Messages.reviewOrder,
            });
        }
    }

    getSubscriptionToCancel() {
        return this.subscriptionToCancel || iFrameUserDataService.getActiveSubscription();
    }

    hasDiscountCodeSupport() {
        const merchant = iFrameMerchantService.getMerchant();
        return merchant && merchant.hasSubscriptionDiscountCodeEnabled();
    }

    updateFromActiveSubscription() {
        const subscription = iFrameUserDataService.getActiveSubscription();
        if (!subscription) {
            this.clear();
        } else {
            subscribePlanController.update({
                subscribePlanState: SUBSCRIBE_PLAN_STATE.editSubscription,
                selectedOffer: null,
                onSelectSuccess: async () => {
                    const offer = subscribePlanController.selectedOffer;
                    console.assert(offer != null, "Selected subscription offer should not be null at this point.");

                    if (!offer.shouldCollectAddress() || (offer.shouldCollectAddress() && addressController.selectedAddress)) {
                        await fetchSubscriptionPrices();
                        Router.changeURL(INLINE_EDIT_SUBSCRIPTION_URLS.subscribe);
                        return;
                    }

                    Router.changeURL(INLINE_EDIT_SUBSCRIPTION_URLS.address);
                },
                onSelectCancel: () => Router.back(),
                applyPolicy: SubscriptionApplyPolicy.NEXT_BILLING_CYCLE,
                discountCode: subscription.getDiscount()?.getCode() || null,
                onCancel: collapseIframe,
            });

            addressController.update({
                addressSectionState: ADDRESS_SECTION_STATE.chooseAddress,
                selectedAddress: subscription.getShippingAddress(),
                onSelectCancel: () => Router.back(),
                onSelectSuccess: async () => {
                    await fetchSubscriptionPrices();
                    Router.changeURL(INLINE_EDIT_SUBSCRIPTION_URLS.subscribe)
                },
                continueLabel: Messages.continue,
            });

            paymentMethodController.update({
                cardSectionState: CARD_SECTION_STATE.chooseCard,
                entry: subscription,
                initialPaymentMethod: subscription.getPaymentMethod(),
                selectedPaymentMethod: subscription.getPaymentMethod(),
                savePaymentMethod: true,
                getChooseCardButtonLabel: () => Messages.updatePaymentMethod,
                getPayWithAddedCardButtonLabel: () => Messages.updatePaymentMethod,
                getScope: () => PaymentMethodStore.Scope.RECURRING_PAYMENT,
                onSelectSuccess: () => Router.changeURL(INLINE_EDIT_SUBSCRIPTION_URLS.subscribe),
            });
        }
    }

    getSubscriptionOfferPrice(offer, discountInfo = null) {
        if (discountInfo) {
            const {discountedBeforeTaxesPrice, discountValue} = discountInfo;
            return discountValue ? offer.formatPrice(discountedBeforeTaxesPrice) : offer.getPrice();
        }

         return offer.getPrice();
    }

    async changeSubscription() {
        const shippingAddress = addressController.selectedAddress;
        const {selectedOffer, discountCode, applyPolicy} = subscribePlanController;
        const discountInfo = subscribePlanController.getDiscountInfo(selectedOffer);
        const activeSubscription = iFrameUserDataService.getActiveSubscription();

        let subscriptionDetails = {
            subscriptionId: activeSubscription.id,
            applyPolicy,
            recurringOfferId: selectedOffer?.id,
            discountCode: discountInfo?.discountValue ? discountCode : null,
            ...(await paymentMethodController.extractPaymentMethodFields()),
        };

        if (activeSubscription.getPaymentMethod() === paymentMethodController.selectedPaymentMethod) {
            subscriptionDetails.preferredPaymentMethodId = null;
        }

        if ((!selectedOffer && activeSubscription.getShippingAddressId()) || selectedOffer?.shouldCollectAddress()) {
            subscriptionDetails.userAddressId = shippingAddress.id;
        }

        LoadingSpinner.show();
        let subscription = null;
        try {
            subscription = await apiChangeSubscription(subscriptionDetails);
        } catch (error) {
            PanelToast.showError(error.message);
            // TODO: Handle update error.
        } finally {
            LoadingSpinner.hide();
        }

        if (!subscription || !subscription.isActive()) {
            console.warn("Subscribe error: ", Messages.subscribeFailed);
            return;
        }
        Router.changeURL(INLINE_EDIT_SUBSCRIPTION_URLS.success);
    }

    async cancel(subscription) {
        LoadingSpinner.show();
        try {
            const subscriptionId = subscription?.id || iFrameUserDataService.getActiveSubscription().id;
            await apiCancelSubscription(subscriptionId);
            Router.changeURL(INLINE_EDIT_SUBSCRIPTION_URLS.cancelSuccess);
        } catch(e) {
            console.warn("Error cancelling subscription ", e);
        } finally {
            LoadingSpinner.hide();
        }
    }

    async getSubscribeEndpointPayload() {
        const shippingAddress = addressController.selectedAddress;
        const {selectedOffer, discountCode} = subscribePlanController;
        const discountInfo = subscribePlanController.getDiscountInfo(selectedOffer);
        const subscriptionDetails = {
            recurringOfferId: selectedOffer.id,
            discountCode: discountInfo?.discountValue ? discountCode : null,
            metadata: {},
            ...(await paymentMethodController.extractPaymentMethodFields()),
        };

        if (selectedOffer.shouldCollectAddress()) {
            subscriptionDetails.userAddressId = shippingAddress.id;
        }

        if (iFrameMerchantService.isMerchant(SOUTHERN_THING_ALIAS)) {
            subscriptionDetails.metadata.shareName = subscribePlanController.shareName;
        }
        return subscriptionDetails;
    }

    async subscribe() {
        LoadingSpinner.show();
        const subscriptionDetails = await this.getSubscribeEndpointPayload();
        dispatchAnalyticsEvent(AnalyticsEventType.ATTEMPT_SUBSCRIBE, {recurringOfferId: subscriptionDetails.recurringOfferId});

        let subscription = null;
        try {
            if (iFrameMerchantService.isCDSPaymentMethod()) {
                subscription = await apiSubscribeWithCds(await this.getSubscribeEndpointPayload());
            } else {
                subscription = await apiSubscribe(await this.getSubscribeEndpointPayload());
            }
        } catch (error) {
            if (error?.message) {
                paymentMethodController.update({paymentError: error});
                Router.changeURL(INLINE_SUBSCRIPTION_URLS.subscribeFailed);
            }
        } finally {
            LoadingSpinner.hide();
        }

        if (!subscription || !subscription.isActive()) {
            console.warn("Subscribe error: ", Messages.subscribeFailed);
            return;
        }
        sendFlowCheckpoint(FLOW_TYPE.subscription, FLOW_CHECKPOINT.complete, {
            subscriptionId: subscription.id,
            offerId: subscription.recurringOfferId,
            coverageId: subscription.coverageId,
            amount: subscription.getPrice().toMainUnitString({includeSymbol: false}),
            currency: subscription.currency.isoCode,
        });
        Router.changeURL(INLINE_SUBSCRIPTION_URLS.subscribeThankYou);
    }

    clear() {
        paymentMethodController.clear();
        subscribePlanController.clear();
        addressController.update({
            addressSectionState: null,
            selectedAddress: null,
        });
        this.subscriptionToCancel = null;
    }
}

export const subscribeFlowState = new SubscribeFlowState();
